import React, { useEffect, useState, useCallback } from "react";
import { Breadcrumb, Layout, Tabs, Card, Typography, Tag, Button, Space, Spin, Descriptions, Switch, Tooltip, Modal, message } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useParams, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { GalleryTypeEnum } from "../../enums/GalleryTypeEnum";
import { IGallery } from "../../services/nestjs-base-api/types";
import { useGetAllGalleriesQuery, useGetGalleryDetailsQuery } from "../../services/nestjs-base-api/admin-api";
import { useToggleGalleryRecognitionMutation, useToggleGalleryPublicVisibilityMutation } from "../../services/nestjs-base-api";
import { ArrowLeftOutlined, FileImageOutlined, InfoCircleOutlined, EditOutlined, EyeOutlined, EyeInvisibleOutlined, GlobalOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const AdminGalleryDetailPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const { galleryId } = useParams<{ galleryId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const [messageApi, contextHolder] = message.useMessage();
  
  // Toggle mutations
  const [toggleGalleryRecognition, { isLoading: isTogglingRecognition }] = useToggleGalleryRecognitionMutation();
  const [toggleGalleryPublicVisibility, { isLoading: isTogglingVisibility }] = useToggleGalleryPublicVisibilityMutation();

  // Fetch all galleries to find the specific one
  const { data: galleriesData, error: galleriesError, isLoading: galleriesLoading } = useGetAllGalleriesQuery({
    jwtToken: user.jwtToken,
  }, { pollingInterval: 600000 });

  // Fetch gallery details including photo count
  const { data: galleryDetails, error: detailsError, isLoading: detailsLoading } = useGetGalleryDetailsQuery({
    jwtToken: user.jwtToken,
    galleryId: parseInt(galleryId || '0')
  }, { skip: !galleryId });

  useEffect(() => {
    if (galleriesError || detailsError) {
      const error = galleriesError || detailsError;
      if (error && 'status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [galleriesError, detailsError, logout]);

  // Find the gallery from the list
  const galleryFromList = galleriesData?.find((g: IGallery) => g.id === parseInt(galleryId || '0'));
  // Get gallery from detailed query if available, otherwise use the one from the list
  const gallery = galleryDetails?.gallery || galleryFromList;

  const isLoading = galleriesLoading || detailsLoading;
  const photoCount = galleryDetails?.photoCount || 0;

  // Function to format date
  const formatDate = (dateString?: string) => {
    return dateString ? dayjs(dateString).format('MMMM D, YYYY HH:mm:ss') : '-';
  };

  // Function to get gallery type display name
  const getGalleryTypeName = (type: string): string => {
    switch (type) {
      case GalleryTypeEnum.Event:
        return intl.formatMessage({ id: 'gallery.type.event' }) || 'Event';
      case GalleryTypeEnum.Reference:
        return intl.formatMessage({ id: 'gallery.type.reference' }) || 'Reference';
      default:
        return type;
    }
  };

  // Function to get gallery type color
  const getGalleryTypeColor = (type: string): string => {
    switch (type) {
      case GalleryTypeEnum.Event:
        return 'blue';
      case GalleryTypeEnum.Reference:
        return 'purple';
      default:
        return 'default';
    }
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };
  
  // Handle toggling facial recognition
  const handleToggleRecognition = useCallback(async (exclude: boolean) => {
    if (!gallery || !galleryId) return;
    
    Modal.confirm({
      title: exclude 
        ? intl.formatMessage({ id: 'gallery.excludeRecognitionTitle' }) || 'Disable Facial Recognition?'
        : intl.formatMessage({ id: 'gallery.includeRecognitionTitle' }) || 'Enable Facial Recognition?',
      content: exclude
        ? intl.formatMessage({ id: 'gallery.excludeRecognitionMessage' }) || 'Excluding this gallery means guests won\'t be able to find photos from this gallery when they upload selfies. Are you sure?'
        : intl.formatMessage({ id: 'gallery.includeRecognitionMessage' }) || 'This will allow guests to find photos from this gallery when they upload selfies. Continue?',
      icon: exclude ? <EyeInvisibleOutlined /> : <EyeOutlined />,
      okText: intl.formatMessage({ id: 'common.yesButton' }) || 'Yes',
      cancelText: intl.formatMessage({ id: 'common.noButton' }) || 'No',
      onOk: async () => {
        try {
          await toggleGalleryRecognition({
            jwtToken: user.jwtToken,
            galleryId: parseInt(galleryId),
            exclude
          }).unwrap();
          
          messageApi.success(
            exclude 
              ? intl.formatMessage({ id: 'gallery.recognitionDisabled' }) || 'Facial recognition disabled for this gallery'
              : intl.formatMessage({ id: 'gallery.recognitionEnabled' }) || 'Facial recognition enabled for this gallery'
          );
        } catch (error) {
          if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
            logout();
          } else {
            messageApi.error(intl.formatMessage({ id: 'gallery.toggleRecognitionError' }) || 'Error updating facial recognition settings');
          }
        }
      }
    });
  }, [gallery, galleryId, toggleGalleryRecognition, user.jwtToken, intl, messageApi, logout]);
  
  // Handle toggling public visibility
  const handleTogglePublicVisibility = useCallback(async (publiclyVisible: boolean) => {
    if (!gallery || !galleryId) return;
    
    Modal.confirm({
      title: publiclyVisible 
        ? intl.formatMessage({ id: 'gallery.makePublicTitle' }) || 'Make Gallery Public?'
        : intl.formatMessage({ id: 'gallery.makePrivateTitle' }) || 'Make Gallery Private?',
      content: publiclyVisible
        ? intl.formatMessage({ id: 'gallery.makePublicMessage' }) || 'This will make the gallery publicly visible to anyone with the link. Continue?'
        : intl.formatMessage({ id: 'gallery.makePrivateMessage' }) || 'This will make the gallery private and only accessible to authenticated users. Continue?',
      icon: publiclyVisible ? <GlobalOutlined /> : <LockOutlined />,
      okText: intl.formatMessage({ id: 'common.yesButton' }) || 'Yes',
      cancelText: intl.formatMessage({ id: 'common.noButton' }) || 'No',
      onOk: async () => {
        try {
          await toggleGalleryPublicVisibility({
            jwtToken: user.jwtToken,
            galleryId: parseInt(galleryId),
            publiclyVisible
          }).unwrap();
          
          messageApi.success(
            publiclyVisible 
              ? intl.formatMessage({ id: 'gallery.madePublic' }) || 'Gallery is now publicly visible'
              : intl.formatMessage({ id: 'gallery.madePrivate' }) || 'Gallery is now private'
          );
        } catch (error) {
          if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
            logout();
          } else {
            messageApi.error(intl.formatMessage({ id: 'gallery.toggleVisibilityError' }) || 'Error updating gallery visibility');
          }
        }
      }
    });
  }, [gallery, galleryId, toggleGalleryPublicVisibility, user.jwtToken, intl, messageApi, logout]);

  if (isLoading) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate('/admin/galleries')} className="breadcrumb-link">
            <FormattedMessage id={'section.galleriesName'} defaultMessage="Galleries" />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'admin.gallery.details'} defaultMessage="Gallery Details" />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div
          style={{
            padding: 24,
            minHeight: '79vh',
            background: 'white',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      </Content>
    );
  }

  if (!gallery) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate('/admin/galleries')} className="breadcrumb-link">
            <FormattedMessage id={'section.galleriesName'} defaultMessage="Galleries" />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'admin.gallery.details'} defaultMessage="Gallery Details" />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div
          style={{
            padding: 24,
            minHeight: '79vh',
            background: 'white',
            borderRadius: 2,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/admin/galleries')}
            >
              <FormattedMessage id={'common.back'} defaultMessage="Back to Galleries" />
            </Button>
          </div>
          <Card>
            <Title level={4}>
              <FormattedMessage id={'admin.gallery.notFound'} defaultMessage="Gallery not found" />
            </Title>
            <Paragraph>
              <FormattedMessage 
                id={'admin.gallery.notFoundDesc'} 
                defaultMessage="The gallery you are looking for does not exist or you don't have permission to view it." 
              />
            </Paragraph>
          </Card>
        </div>
      </Content>
    );
  }

  return (
    <Content style={{ margin: '0 16px' }}>
      {contextHolder}
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate('/admin/galleries')} className="breadcrumb-link">
          <FormattedMessage id={'section.galleriesName'} defaultMessage="Galleries" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'admin.gallery.details'} defaultMessage="Gallery Details" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <div
        style={{
          padding: 24,
          minHeight: '79vh',
          background: 'white',
          borderRadius: 2,
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/admin/galleries')}
          >
            <FormattedMessage id={'common.back'} defaultMessage="Back to Galleries" />
          </Button>
        </div>

        <Card 
          title={
            <Space>
              <Title level={2}>{gallery.name}</Title>
              <Tag color={getGalleryTypeColor(gallery.type)} style={{ marginLeft: 8 }}>
                {getGalleryTypeName(gallery.type)}
              </Tag>
              {gallery?.excludeFromRecognition && (
                <Tooltip title={intl.formatMessage({ id: 'gallery.recognitionDisabledTooltip' }) || 'Guests cannot find photos from this gallery using selfies'}>
                  <EyeInvisibleOutlined style={{ fontSize: 16, color: '#ff7875' }} />
                </Tooltip>
              )}
              {gallery?.isPubliclyVisible && (
                <Tooltip title={intl.formatMessage({ id: 'gallery.publiclyVisibleTooltip' }) || 'This gallery is visible to anyone with the link'}>
                  <GlobalOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                </Tooltip>
              )}
            </Space>
          } 
          extra={
            <Space>
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => navigate(`/admin/galleries/${gallery.id}/edit`)}
              >
                <FormattedMessage id={'common.edit'} defaultMessage="Edit" />
              </Button>
              <Button 
                onClick={() => navigate(`/admin/events/${gallery.eventId}`)}
              >
                <FormattedMessage id={'admin.gallery.viewEvent'} defaultMessage="View Event" />
              </Button>
            </Space>
          }
        >
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <Tabs.TabPane 
              tab={
                <span>
                  <InfoCircleOutlined />
                  <FormattedMessage id={'gallery.tabs.details'} defaultMessage="Details" />
                </span>
              } 
              key="details"
            >
              {/* Visibility Controls */}
              <div style={{ marginBottom: 24 }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {/* Facial Recognition Toggle */}
                  <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space align="center">
                        <Tooltip 
                          title={
                            gallery?.excludeFromRecognition 
                              ? intl.formatMessage({ id: 'gallery.recognitionDisabledTooltip' }) || 'Guests cannot find photos from this gallery using selfies'
                              : intl.formatMessage({ id: 'gallery.recognitionEnabledTooltip' }) || 'Guests can find photos from this gallery using selfies'
                          }
                        >
                          <Space>
                            {gallery?.excludeFromRecognition ? <EyeInvisibleOutlined style={{ fontSize: 18, color: '#ff7875' }} /> : <EyeOutlined style={{ fontSize: 18, color: '#52c41a' }} />}
                            <Text strong>{intl.formatMessage({ id: 'gallery.facialRecognition' }) || 'Facial Recognition'}</Text>
                          </Space>
                        </Tooltip>
                        {gallery?.excludeFromRecognition && (
                          <Tag color="warning">{intl.formatMessage({ id: 'gallery.notSearchableByGuests' }) || 'Not searchable by guests'}</Tag>
                        )}
                      </Space>
                      <Switch
                        checked={!gallery?.excludeFromRecognition}
                        onChange={(checked) => handleToggleRecognition(!checked)}
                        loading={isTogglingRecognition}
                        checkedChildren={<EyeOutlined />}
                        unCheckedChildren={<EyeInvisibleOutlined />}
                      />
                    </Space>
                  </Card>
                  
                  {/* Public Visibility Toggle */}
                  <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space align="center">
                        <Tooltip 
                          title={
                            gallery?.isPubliclyVisible 
                              ? intl.formatMessage({ id: 'gallery.publiclyVisibleTooltip' }) || 'This gallery is visible to anyone with the link'
                              : intl.formatMessage({ id: 'gallery.privateTooltip' }) || 'This gallery is only visible to authenticated users'
                          }
                        >
                          <Space>
                            {gallery?.isPubliclyVisible ? <GlobalOutlined style={{ fontSize: 18, color: '#52c41a' }} /> : <LockOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />}
                            <Text strong>{intl.formatMessage({ id: 'gallery.publiclyVisible' }) || 'Publicly Visible'}</Text>
                          </Space>
                        </Tooltip>
                        {!gallery?.isPubliclyVisible && (
                          <Tag color="default">{intl.formatMessage({ id: 'gallery.privateAccess' }) || 'Private access only'}</Tag>
                        )}
                      </Space>
                      <Switch
                        checked={gallery?.isPubliclyVisible || false}
                        onChange={(checked) => handleTogglePublicVisibility(checked)}
                        loading={isTogglingVisibility}
                        checkedChildren={<GlobalOutlined />}
                        unCheckedChildren={<LockOutlined />}
                      />
                    </Space>
                  </Card>
                </Space>
              </div>
              
              <Descriptions layout="vertical" bordered column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.id' }) || "ID"}>
                  {gallery.id}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.type' }) || "Type"}>
                  <Tag color={getGalleryTypeColor(gallery.type)}>
                    {getGalleryTypeName(gallery.type)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.event' }) || "Event"}>
                  {gallery.event?.name || `Event ID: ${gallery.eventId}`}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.photoCount' }) || "Photos"}>
                  {photoCount}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.created' }) || "Created"} span={2}>
                  {formatDate(gallery.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.updated' }) || "Updated"} span={2}>
                  {formatDate(gallery.updatedAt)}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.hash' }) || "Hash"} span={2}>
                  {gallery.hash}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'gallery.description' }) || "Description"} span={4}>
                  {gallery.description || intl.formatMessage({ id: 'gallery.noDescription' }) || "No description provided"}
                </Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <span>
                  <FileImageOutlined />
                  <FormattedMessage id={'gallery.tabs.photos'} defaultMessage="Photos" />
                </span>
              } 
              key="photos"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  <FormattedMessage id={'admin.gallery.photosDescription'} defaultMessage="This gallery contains {count} photos." values={{ count: photoCount }} />
                </Paragraph>
                
                {photoCount > 0 ? (
                  <Button 
                    type="primary" 
                    onClick={() => {
                      // Navigate to photos view (would need to be implemented)
                      // For now, just show an info message
                      // In a real implementation, this would navigate to a specialized photo viewer or manager
                    }}
                  >
                    <FormattedMessage id={'admin.gallery.viewPhotos'} defaultMessage="View Photos" />
                  </Button>
                ) : (
                  <Paragraph>
                    <FormattedMessage id={'admin.gallery.noPhotos'} defaultMessage="This gallery contains no photos." />
                  </Paragraph>
                )}
              </Space>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    </Content>
  );
};

export default AdminGalleryDetailPage;